import {
  FetchNextPageOptions,
  InfiniteData,
  InfiniteQueryObserverResult,
} from '@tanstack/react-query';
import React, { FC, useEffect, useRef, useState } from 'react';
import { Control, useController } from 'react-hook-form';
import InfiniteScroll from 'react-infinite-scroll-component';

import { ChevronDownIcon } from '../../../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../../../components/app-formatted-message/index';
import {
  ComboboxButton,
  ComboboxInput,
  ComboboxOptions,
  ComboboxOptionsPortalWrapper,
  FormCombobox,
  FormComboboxOption,
} from '../../../../../../components/combobox/index';
import Loader from '../../../../../../components/loader/index';
import { Tooltip } from '../../../../../../components/tooltip/index';
import { useScreenSize } from '../../../../../../hooks/index';
import { useInfiniteStakeholders } from '../../../../../../hooks/stakeholder/use-infinite-stakeholders';
import { temporaryScenarioStakeholdersStore } from '../../../../../../storage/local/temporary-scenario-stakeholders.util';
import { Stakeholder } from '../../../../../../types/stakeholder.types';
import { removeSpaces } from '../../../../../../utils/remove-spaces.util';
import { FundraisingFormSchema } from '../../../fundraising-validation';

export type StakeholderValue = {
  fullName: string;
  id: string;
};

export type StakeholderItemsListProps = {
  control: Control<FundraisingFormSchema>;
  data: Stakeholder[];
  fetchNextPage: (options?: FetchNextPageOptions) => Promise<
    InfiniteQueryObserverResult<
      InfiniteData<
        {
          stakeholders: Stakeholder[];
          totalPages: number;
        },
        unknown
      >,
      Error
    >
  >;
  fieldName: string;
  hasNextPage: boolean;
  search: string;
  tempStakeholders: string[];
};

const StakeholderItemsList: FC<StakeholderItemsListProps> = ({
  data,
  control,
  search,
  fieldName,
  hasNextPage,
  fetchNextPage,
  tempStakeholders,
}) => {
  const { scrollableTarget } = useScreenSize();
  const isTemporaryAlreadyAdded =
    (search && tempStakeholders.includes(search.toLowerCase())) ||
    data.some(({ fullName }) => fullName.toLocaleLowerCase() === search.toLocaleLowerCase());

  return (
    <InfiniteScroll
      className="flex w-full flex-col gap-2"
      dataLength={data.length}
      hasMore={hasNextPage}
      loader={<Loader />}
      next={fetchNextPage}
      scrollableTarget={scrollableTarget}
    >
      {data.length > 0 ? (
        <>
          {data
            .sort((a, b) => a.fullName.toLowerCase().localeCompare(b.fullName.toLowerCase()))
            .map(({ fullName, id }, i) => (
              <FormComboboxOption
                className="w-full"
                control={control}
                key={`${fullName}_${i}`}
                name={fieldName as keyof FundraisingFormSchema}
                value={
                  {
                    id,
                    fullName,
                  } as unknown as FundraisingFormSchema[keyof FundraisingFormSchema]
                }
              >
                <Tooltip content={fullName}>
                  <span className="truncate text-sm text-gray-700">{fullName}</span>
                </Tooltip>
              </FormComboboxOption>
            ))}
        </>
      ) : (
        <span className="ml-2 px-2 py-[6px] text-sm font-normal text-gray-700">
          <AppFormattedMessage id="NO_RESULTS_FOUND" />
        </span>
      )}
      {search && !isTemporaryAlreadyAdded && (
        <div className="flex flex-col border-t-[1px] border-gray-100">
          <span className="px-2 py-[6px] text-label-sm font-medium text-gray-500">
            <AppFormattedMessage id="ADD_TEMPORARY" />
          </span>
          <FormComboboxOption
            className="w-full"
            control={control}
            name={fieldName as keyof FundraisingFormSchema}
            value={
              {
                id: 'new',
                fullName: removeSpaces(search),
              } as FundraisingFormSchema[keyof FundraisingFormSchema]
            }
          >
            <span className="truncate text-sm text-gray-700">{search}</span>
          </FormComboboxOption>
        </div>
      )}
    </InfiniteScroll>
  );
};

export type StakeholderComboboxProps = {
  companyId: string;
  control: Control<FundraisingFormSchema>;
  fieldName: string;
  scenarioId: string;
};

export const StakeholderCombobox: FC<StakeholderComboboxProps> = ({
  control,
  companyId,
  fieldName,
  scenarioId,
}) => {
  const [tempStakeholders, setTempStakeholders] = useState<string[]>([]);
  const inputRef = useRef<HTMLDivElement | null>(null);

  const { field, fieldState } = useController({
    control,
    name: fieldName as keyof FundraisingFormSchema,
  });
  const value = (field?.value as StakeholderValue)?.fullName || '';

  const { stakeholders, fetchNextPage, hasNextPage } = useInfiniteStakeholders({
    search: '',
    companyId,
  });

  useEffect(() => {
    const data = temporaryScenarioStakeholdersStore.get();
    setTempStakeholders(data?.[scenarioId] || []);
  }, [scenarioId]);

  if (!stakeholders) return;
  const filteredStakeholders = stakeholders.filter((stakeholder) =>
    stakeholder.fullName.toLowerCase().includes(value.trim().toLowerCase()),
  );

  const onChange = (value: string) => {
    const trimmed = removeSpaces(value);
    field.onChange({ fullName: trimmed });
  };

  const handleBlur = () => {
    const isTemporaryAlreadyAdded = tempStakeholders
      ? tempStakeholders.includes(value.toLowerCase())
      : false;
    if (value && !isTemporaryAlreadyAdded) {
      field.onChange({ id: 'new', fullName: removeSpaces(value) });
    }
  };

  return (
    <>
      <FormCombobox
        className="relative"
        control={control}
        name={fieldName as keyof FundraisingFormSchema}
      >
        <div className="relative" ref={inputRef}>
          <ComboboxInput
            {...field}
            autoComplete="off"
            errorMessage={(fieldState.error as { name?: { message?: string } })?.name?.message}
            icon={<ChevronDownIcon className="mt-3" />}
            onBlur={handleBlur}
            onChange={onChange}
            value={value}
            wrapperClassName="h-10"
          />
          <ComboboxButton className="absolute left-0 top-0 z-20 h-full w-full" />
        </div>
        <ComboboxOptionsPortalWrapper
          anchorRef={inputRef}
          itemsCount={filteredStakeholders?.length || 0}
        >
          <ComboboxOptions className="z-20 max-h-[150px] w-full overflow-x-hidden">
            <StakeholderItemsList
              control={control}
              data={filteredStakeholders}
              fetchNextPage={fetchNextPage}
              fieldName={fieldName}
              hasNextPage={hasNextPage}
              search={value}
              tempStakeholders={tempStakeholders}
            />
          </ComboboxOptions>
        </ComboboxOptionsPortalWrapper>
      </FormCombobox>
    </>
  );
};
